import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { format } from 'date-fns'
import { useLoans } from '../../hooks/useLoans'
import { useBooks } from '../../hooks/useBooks'
import { useUsers } from '../../hooks/useUsers'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import AddBookModal from './AddBookModal'
import AddUserModal from './AddUserModal'
import NewCheckoutModal from './NewCheckoutModal'
import * as FiIcons from 'react-icons/fi'
import toast from 'react-hot-toast'

const {
  FiUsers, FiBook, FiClock, FiSettings, FiPlus, FiChevronRight, 
  FiTrash2, FiShield, FiUser, FiSend, FiLoader, FiX, FiCheck, 
  FiEdit3, FiCornerDownLeft, FiLayers, FiSearch, FiFilter, 
  FiChevronDown, FiChevronUp, FiMail, FiZap
} = FiIcons

const AdminPanel = () => {
  const { profile: adminProfile } = useAuth()
  const [activeTab, setActiveTab] = useState('overview')
  const [isAddBookOpen, setIsAddBookOpen] = useState(false)
  const [isAddUserOpen, setIsAddUserOpen] = useState(false)
  const [isNewCheckoutOpen, setIsNewCheckoutOpen] = useState(false)
  const [editingBook, setEditingBook] = useState(null)
  const [resendingId, setResendingId] = useState(null)
  const [returningLoanId, setReturningLoanId] = useState(null)
  const [showCategoryFilter, setShowCategoryFilter] = useState(false)

  // Data Hooks - Optimized to only fetch active loans (includeReturned = false)
  const { loans, refetch: refetchLoans } = useLoans(null, false)
  const { books, addBook, updateBook, returnBook, checkoutBook, dynamicCategories } = useBooks()
  const { users, invites, resendInvite, deleteInvite, refetch: refetchUsers } = useUsers()

  // Inventory State
  const [inventorySearch, setInventorySearch] = useState('')
  const [inventoryCategories, setInventoryCategories] = useState([])
  const [inventorySort, setInventorySort] = useState({ field: 'title', direction: 'asc' })

  // Since includeReturned is false, 'loans' already only contains active items
  const activeLoans = useMemo(() => loans.filter(loan => !loan.returned_date), [loans])

  const filteredBooks = useMemo(() => {
    let result = [...books]
    if (inventorySearch) {
      const search = inventorySearch.toLowerCase()
      result = result.filter(b => 
        b.title.toLowerCase().includes(search) || 
        b.author.toLowerCase().includes(search) || 
        b.isbn?.toLowerCase().includes(search)
      )
    }
    if (inventoryCategories.length > 0) {
      result = result.filter(b => inventoryCategories.includes(b.category))
    }
    result.sort((a, b) => {
      const fieldA = (a[inventorySort.field] || '').toString().toLowerCase()
      const fieldB = (b[inventorySort.field] || '').toString().toLowerCase()
      return inventorySort.direction === 'asc' 
        ? (fieldA > fieldB ? 1 : -1) 
        : (fieldA < fieldB ? 1 : -1)
    })
    return result
  }, [books, inventorySearch, inventoryCategories, inventorySort])

  const toggleSort = (field) => {
    setInventorySort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const toggleInventoryCategory = (cat) => {
    setInventoryCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    )
  }

  const handleReturnAction = async (loan) => {
    setReturningLoanId(loan.id)
    const res = await returnBook(loan.book_id, loan.id)
    if (res.success) {
      toast.success("Book checked back in")
      await refetchLoans()
    } else {
      toast.error(res.error)
    }
    setReturningLoanId(null)
  }

  const handleManualCheckout = async (bookId, userId) => {
    const res = await checkoutBook(bookId, userId)
    if (res.success) {
      await refetchLoans()
      return { success: true }
    }
    return res
  }

  const handleResend = async (invite) => {
    setResendingId(invite.id)
    const res = await resendInvite(invite.id, invite.email, invite.full_name, invite.role)
    setResendingId(null)
    if (res.success) toast.success("Invitation re-dispatched!")
    else toast.error(res.error)
  }

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this invitation? The link will no longer work.")) return
    const res = await deleteInvite(id)
    if (res.success) toast.success("Invitation revoked")
    else toast.error(res.error)
  }

  const renderInventory = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-3xl border-2 border-white-400 p-4 shadow-sm flex flex-col md:flex-row gap-4 items-center relative">
        <div className="relative flex-1 w-full">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-white-400 w-4 h-4" />
          <input 
            type="text" 
            placeholder="Search inventory..." 
            value={inventorySearch}
            onChange={(e) => setInventorySearch(e.target.value)}
            className="w-full pl-11 pr-10 py-2.5 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all text-xs font-bold" 
          />
          {inventorySearch && (
            <button 
              onClick={() => setInventorySearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-white-400 hover:text-hot_berry transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2 w-full md:w-auto relative">
          <div className="relative flex-1 md:flex-none">
            <button 
              onClick={() => setShowCategoryFilter(!showCategoryFilter)}
              className={`w-full md:w-48 px-4 py-2.5 border-2 rounded-xl text-xs font-black uppercase tracking-widest flex items-center justify-between transition-all ${inventoryCategories.length > 0 ? 'bg-dark_cyan-900 border-dark_cyan text-dark_cyan' : 'bg-white-500 border-white-400 text-space_indigo hover:border-dark_cyan'}`}
            >
              <div className="flex items-center truncate">
                <SafeIcon icon={FiFilter} className="w-3.5 h-3.5 mr-2" />
                <span className="truncate">
                  {inventoryCategories.length === 0 ? 'All Categories' : `${inventoryCategories.length} Selected`}
                </span>
              </div>
              <SafeIcon icon={FiChevronDown} className={`w-3.5 h-3.5 transition-transform ${showCategoryFilter ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {showCategoryFilter && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowCategoryFilter(false)} />
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-white-400 rounded-2xl shadow-2xl z-20 max-h-72 overflow-y-auto p-2 scrollbar-hide min-w-[200px]"
                  >
                    <div className="p-2 space-y-1">
                      {inventoryCategories.length > 0 && (
                        <button 
                          onClick={() => {setInventoryCategories([]); setShowCategoryFilter(false);}}
                          className="w-full text-center py-2 mb-2 rounded-lg bg-hot_berry-900 text-hot_berry text-[9px] font-black uppercase tracking-widest border border-dashed border-hot_berry-400 hover:bg-hot_berry hover:text-white transition-all flex items-center justify-center gap-2"
                        >
                          <SafeIcon icon={FiX} className="w-3 h-3" />
                          Clear All Selected
                        </button>
                      )}
                      {dynamicCategories.map(cat => (
                        <button
                          key={cat}
                          onClick={() => toggleInventoryCategory(cat)}
                          className={`w-full text-left px-3 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center justify-between transition-colors ${inventoryCategories.includes(cat) ? 'bg-dark_cyan text-white shadow-md' : 'hover:bg-white-500 text-white-300'}`}
                        >
                          <span className="truncate mr-2">{cat}</span>
                          {inventoryCategories.includes(cat) && <SafeIcon icon={FiCheck} className="w-3 h-3 shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
          <button 
            onClick={() => {setEditingBook(null); setIsAddBookOpen(true);}}
            className="bg-dark_cyan text-white px-4 py-2.5 rounded-xl flex items-center gap-2 text-xs font-black uppercase tracking-widest shadow-lg shadow-dark_cyan/20 shrink-0"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            Add Volume
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border-2 border-white-400 overflow-hidden shadow-sm">
        <div className="hidden md:grid grid-cols-12 gap-4 p-5 bg-white-500 border-b-2 border-white-400 text-[10px] font-black text-white-300 uppercase tracking-widest">
          <div className="col-span-5 cursor-pointer hover:text-dark_cyan flex items-center" onClick={() => toggleSort('title')}>
            Title / Author
            {inventorySort.field === 'title' && (inventorySort.direction === 'asc' ? <SafeIcon icon={FiChevronUp} className="ml-1" /> : <SafeIcon icon={FiChevronDown} className="ml-1" />)}
          </div>
          <div className="col-span-2 cursor-pointer hover:text-dark_cyan flex items-center" onClick={() => toggleSort('category')}>
            Category
            {inventorySort.field === 'category' && (inventorySort.direction === 'asc' ? <SafeIcon icon={FiChevronUp} className="ml-1" /> : <SafeIcon icon={FiChevronDown} className="ml-1" />)}
          </div>
          <div className="col-span-2 cursor-pointer hover:text-dark_cyan flex items-center" onClick={() => toggleSort('published_year')}>
            Year
            {inventorySort.field === 'published_year' && (inventorySort.direction === 'asc' ? <SafeIcon icon={FiChevronUp} className="ml-1" /> : <SafeIcon icon={FiChevronDown} className="ml-1" />)}
          </div>
          <div className="col-span-2">Status / Stock</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        <div className="divide-y-2 divide-white-400">
          {filteredBooks.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-[10px] font-black uppercase text-white-300 tracking-widest mb-4">No matching volumes found</p>
              {(inventorySearch || inventoryCategories.length > 0) && (
                <button 
                  onClick={() => {setInventorySearch(''); setInventoryCategories([]);}}
                  className="text-xs font-black text-hot_berry uppercase tracking-widest hover:underline"
                >
                  Clear all active filters
                </button>
              )}
            </div>
          ) : (
            filteredBooks.map(book => (
              <div key={book.id} className="p-4 md:grid md:grid-cols-12 md:gap-4 md:items-center hover:bg-white-500 transition-colors">
                <div className="col-span-5 flex items-center min-w-0 mb-3 md:mb-0">
                  <div className="w-10 h-14 bg-white-500 border-2 border-white-400 rounded-lg shrink-0 flex items-center justify-center mr-4 overflow-hidden">
                    {book.cover_image ? <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" /> : <SafeIcon icon={FiBook} className="w-5 h-5 text-white-400" />}
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-black text-space_indigo text-xs uppercase truncate leading-tight">{book.title}</h4>
                    <p className="text-[9px] text-white-300 font-bold uppercase truncate mt-0.5">{book.author}</p>
                  </div>
                </div>
                <div className="col-span-2 hidden md:block">
                  <span className="text-[10px] font-black text-space_indigo uppercase tracking-widest bg-white-500 px-2 py-1 rounded-lg border border-white-400">{book.category}</span>
                </div>
                <div className="col-span-2 hidden md:block text-[10px] font-bold text-white-300 uppercase">{book.published_year || 'N/A'}</div>
                <div className="col-span-2 flex items-center gap-3">
                  <div className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-tighter border-2 ${book.isAvailable ? 'bg-dark_cyan-900 text-dark_cyan border-dark_cyan-800' : 'bg-hot_berry-900 text-hot_berry border-hot_berry-800'}`}>
                    {book.isAvailable ? 'In Stock' : 'Out'}
                  </div>
                  <div className="text-[9px] font-black text-white-300 uppercase">{book.available_copies} / {book.total_copies}</div>
                </div>
                <div className="col-span-1 flex justify-end">
                  <button 
                    onClick={() => {setEditingBook(book); setIsAddBookOpen(true);}}
                    className="p-2 text-white-300 hover:text-space_indigo bg-white border border-white-400 rounded-lg transition-all shadow-sm"
                  >
                    <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )

  const renderUsers = () => (
    <div className="space-y-8">
      {invites.length > 0 && (
        <div className="bg-white rounded-3xl border-2 border-white-400 overflow-hidden shadow-sm">
          <div className="p-5 border-b-2 border-white-400 bg-white-500 flex items-center justify-between">
            <h3 className="text-xs font-black text-space_indigo uppercase tracking-widest flex items-center">
              <SafeIcon icon={FiMail} className="w-4 h-4 mr-2 text-hot_berry" />
              Pending Invitations ({invites.length})
            </h3>
          </div>
          <div className="divide-y-2 divide-white-400">
            {invites.map(invite => (
              <div key={invite.id} className="p-4 md:p-6 hover:bg-white-500 transition-colors">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h4 className="font-black text-space_indigo text-xs uppercase truncate leading-tight">{invite.full_name}</h4>
                    <p className="text-[9px] text-white-300 font-bold uppercase truncate mt-0.5">{invite.email}</p>
                    <span className="text-[8px] font-black text-hot_berry uppercase tracking-[0.2em] mt-2 block">Role: {invite.role}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleResend(invite)}
                      disabled={resendingId === invite.id}
                      className="p-2.5 bg-white-500 border-2 border-white-400 rounded-xl text-white-300 hover:text-dark_cyan hover:border-dark_cyan transition-all"
                      title="Resend Email"
                    >
                      {resendingId === invite.id ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /> : <SafeIcon icon={FiSend} className="w-4 h-4" />}
                    </button>
                    <button 
                      onClick={() => handleRevoke(invite.id)}
                      className="p-2.5 bg-white-500 border-2 border-white-400 rounded-xl text-white-300 hover:text-hot_berry hover:border-hot_berry transition-all"
                      title="Revoke Invite"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl border-2 border-white-400 overflow-hidden shadow-sm">
        <div className="p-5 border-b-2 border-white-400 bg-white-500 flex items-center justify-between">
          <h3 className="text-xs font-black text-space_indigo uppercase tracking-widest">Active Members ({users.length})</h3>
          <button 
            onClick={() => setIsAddUserOpen(true)}
            className="bg-hot_berry text-white p-2 rounded-xl shadow-lg shadow-hot_berry/20"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
          </button>
        </div>
        <div className="divide-y-2 divide-white-400">
          {users.map(user => (
            <div key={user.id} className="p-4 md:p-6 hover:bg-white-500 transition-colors">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center min-w-0">
                  <div className="w-10 h-10 bg-white-500 border-2 border-white-400 rounded-xl flex items-center justify-center mr-4 shrink-0">
                    <SafeIcon icon={FiUser} className="w-5 h-5 text-white-300" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-black text-space_indigo text-xs uppercase truncate leading-tight">
                      {user.full_name} {user.id === adminProfile?.id && "(You)"}
                    </h4>
                    <p className="text-[9px] text-white-300 font-bold uppercase truncate mt-0.5">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest border-2 ${user.role === 'admin' ? 'bg-space_indigo text-white border-space_indigo' : user.role === 'staff' ? 'bg-dark_cyan-900 text-dark_cyan border-dark_cyan-800' : 'bg-white-500 text-white-300 border-white-400'}`}>
                    <SafeIcon icon={FiShield} className="w-3 h-3 mr-1.5" />
                    {user.role}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              {[
                { icon: FiBook, label: 'Unique Titles', val: books.length, bgClass: 'bg-dark_cyan-900', textClass: 'text-dark_cyan' },
                { icon: FiLayers, label: 'Total Copies', val: books.reduce((acc, b) => acc + (b.total_copies || 0), 0), bgClass: 'bg-space_indigo-900', textClass: 'text-space_indigo' },
                { icon: FiUsers, label: 'Members', val: users.length, bgClass: 'bg-hot_berry-900', textClass: 'text-hot_berry' },
                { icon: FiMail, label: 'Pending Invites', val: invites.length, bgClass: 'bg-white-500', textClass: 'text-white-300' }
              ].map((stat, i) => (
                <motion.div key={i} className="bg-white rounded-2xl border-2 border-white-400 p-4 shadow-sm">
                  <div className={`${stat.bgClass} w-10 h-10 rounded-xl flex items-center justify-center mb-3`}>
                    <SafeIcon icon={stat.icon} className={`w-5 h-5 ${stat.textClass}`} />
                  </div>
                  <p className="text-[10px] font-black text-white-300 uppercase tracking-widest truncate">{stat.label}</p>
                  <p className="text-xl md:text-2xl font-black text-space_indigo">{stat.val}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )
      case 'loans':
        return (
          <div className="space-y-4">
            <div className="bg-white rounded-3xl border-2 border-white-400 overflow-hidden shadow-sm">
              <div className="p-5 border-b-2 border-white-400 bg-white-500 flex items-center justify-between">
                <h3 className="text-xs font-black text-space_indigo uppercase tracking-widest">Active Loans ({activeLoans.length})</h3>
                <button 
                  onClick={() => setIsNewCheckoutOpen(true)}
                  className="bg-dark_cyan text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center shadow-lg shadow-dark_cyan/20"
                >
                  <SafeIcon icon={FiZap} className="w-3.5 h-3.5 mr-2" />
                  Manual Checkout
                </button>
              </div>
              <div className="divide-y-2 divide-white-400">
                {activeLoans.length === 0 ? (
                  <div className="py-20 text-center">
                    <p className="text-[10px] font-black uppercase text-white-300 tracking-widest">No current loans</p>
                  </div>
                ) : (
                  activeLoans.map(loan => (
                    <div key={loan.id} className="p-4 md:p-6 hover:bg-white-500 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <h4 className="font-black text-space_indigo text-sm uppercase leading-tight truncate">{loan.books.title}</h4>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <p className="text-[10px] text-white-300 font-bold uppercase">Borrower: {loan.profiles.full_name}</p>
                            <span className="text-[9px] text-white-400 font-bold uppercase">• Due {format(new Date(loan.due_date), 'MMM d')}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest border-2 ${loan.isOverdue ? 'bg-hot_berry text-white border-hot_berry' : 'bg-dark_cyan-900 text-dark_cyan border-dark_cyan-800'}`}>
                            {loan.isOverdue ? 'Overdue' : 'On Time'}
                          </span>
                          <button 
                            onClick={() => handleReturnAction(loan)}
                            disabled={returningLoanId === loan.id}
                            className="flex items-center px-4 py-2 bg-space_indigo text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-md hover:bg-space_indigo-400 transition-all disabled:opacity-50"
                          >
                            {returningLoanId === loan.id 
                              ? <SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin mr-2" /> 
                              : <SafeIcon icon={FiCornerDownLeft} className="w-3.5 h-3.5 mr-2" />
                            }
                            Check In
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )
      case 'inventory':
        return renderInventory()
      case 'users':
        return renderUsers()
      default:
        return null
    }
  }

  const tabs = [
    { id: 'overview', label: 'Stats', icon: FiSettings },
    { id: 'loans', label: 'Loans', icon: FiBook },
    { id: 'inventory', label: 'Inventory', icon: FiBook },
    { id: 'users', label: 'Users', icon: FiUsers }
  ]

  return (
    <div className="space-y-6">
      <div className="overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
        <div className="bg-white-500 p-1.5 rounded-2xl border-2 border-white-400 flex min-w-max md:min-w-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab.id ? 'bg-space_indigo text-white shadow-lg' : 'text-white-300 hover:text-space_indigo'}`}
            >
              <SafeIcon icon={tab.icon} className="w-3.5 h-3.5 mr-2" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.2 }}
        >
          {renderTabContent()}
        </motion.div>
      </AnimatePresence>

      <AddBookModal 
        isOpen={isAddBookOpen} 
        onClose={() => {setIsAddBookOpen(false); setEditingBook(null);}} 
        onAdd={addBook}
        onUpdate={updateBook}
        editBook={editingBook}
        categories={dynamicCategories}
      />
      <AddUserModal 
        isOpen={isAddUserOpen} 
        onClose={() => {setIsAddUserOpen(false); refetchUsers();}} 
      />
      <NewCheckoutModal 
        isOpen={isNewCheckoutOpen} 
        onClose={() => setIsNewCheckoutOpen(false)}
        books={books}
        users={users}
        onCheckout={handleManualCheckout}
      />
    </div>
  )
}

export default AdminPanel